import { useCallback, useEffect, useState } from "react";
import { Image, Spin } from "antd";
import { DownloadButton } from "./DownloadAction";
import DriveFolderBrowser from "./DriveFolderBrowser";
import FeedbackPrompt from "../feedback/FeedbackPrompt";
import { getImageViewUrl, getProxyDownloadUrl } from "../../utils/deliveryApi";
import { downloadDeliveryFile } from "../../utils/deliveryDownload";
import { getFolderDisplayName } from "../../utils/deliveryPath";
import { getAlbumName } from "../../utils/deliveryToken";
import { useTranslation } from "../../i18n";

const IMAGE_EXT = new Set(["jpg", "jpeg", "png", "webp", "gif", "heic", "heif", "avif", "bmp", "tiff"]);

function isImageFileName(name) {
    const ext = name.split(".").pop()?.toLowerCase();
    return Boolean(ext && IMAGE_EXT.has(ext));
}

function SingleFileView({ fileKey, linkId, albumName }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const imageUrl = getImageViewUrl(fileKey);
    const fileName = fileKey.split("/").pop() || "download";
    const isImage = isImageFileName(fileName);

    useEffect(() => {
        if (!isImage) {
            setLoading(false);
        }
    }, [isImage]);

    const handleLoad = useCallback(() => {
        setLoading(false);
        setFailed(false);
    }, []);

    const handleError = useCallback(() => {
        setLoading(false);
        setFailed(true);
    }, []);

    const handleDownload = useCallback(async () => {
        try {
            // 单文件交付链接没有独立的相册根路径，key 本身就是被授权访问的边界。
            await downloadDeliveryFile({
                id: fileKey,
                name: fileName,
                downloadUrl: getImageViewUrl(fileKey),
                proxyDownloadUrl: getProxyDownloadUrl(fileKey, fileKey),
            });
        } catch {
            // downloadDeliveryFile throws on failure
        }
    }, [fileKey, fileName]);

    if (failed) {
        return (
            <div className="delivery-embed delivery-embed--centered">
                <p>{t("delivery.embed.fileLoadFailed")}</p>
                <p className="delivery-embed__hint">{t("common.networkTroubleshootHint")}</p>
                <DownloadButton onClick={handleDownload} />
            </div>
        );
    }

    return (
        <div className="delivery-embed delivery-embed--single">
            {loading && (
                <div className="delivery-embed__loading">
                    <Spin size="large" />
                    <p>{t("delivery.embed.loading")}</p>
                </div>
            )}
            {isImage ? (
                <Image
                    src={imageUrl}
                    alt={fileName}
                    className={`delivery-embed__single-image${loading ? " delivery-embed__frame--hidden" : ""}`}
                    onLoad={handleLoad}
                    onError={handleError}
                    preview
                />
            ) : (
                !loading && <p className="delivery-embed__file-name">{fileName}</p>
            )}
            {!loading && (
                <div className="delivery-embed__single-actions">
                    <DownloadButton onClick={handleDownload} />
                </div>
            )}
            {linkId ? (
                <FeedbackPrompt
                    sourceType="delivery"
                    linkId={linkId}
                    albumPath={fileKey}
                    albumName={albumName || fileName}
                />
            ) : null}
        </div>
    );
}

function DriveEmbed({ resource, linkId }) {
    const { t } = useTranslation();

    if (resource.t === "f") {
        const fileName = resource.i.split("/").pop() || "download";
        const displayName =
            getAlbumName(resource, getFolderDisplayName(resource.i)) || fileName;
        return (
            <SingleFileView
                fileKey={resource.i}
                linkId={linkId}
                albumName={displayName}
            />
        );
    }

    const displayName =
        getAlbumName(resource, getFolderDisplayName(resource.i)) ||
        t("delivery.actions.defaultAlbumName");

    return (
        <DriveFolderBrowser
            albumPath={resource.i}
            albumName={displayName}
            downloadAll={resource.downloadAll || null}
            linkId={linkId}
            sourceType="delivery"
        />
    );
}

export default DriveEmbed;
